import { back, backHandledByScreen, currentRouteName, dispatchKey, navigate, onRouteChange, type Route } from '@/app'
import { type ContentCard, isUpcomingAvailable, searchAll, type SearchPerson, type SearchResult } from '@/lib/catalog'
import { byId, clear, el, setClass, setText } from '@/lib/dom'
import { IMAGE_WIDTH, mediaUrl } from '@/lib/images'
import { T } from '@/lib/i18n'
import { isNavigationKey, registerPlatformKeys, toRemoteKey } from '@/lib/keys'
import { LANGUAGES, readLanguage, reloadForLanguage, writeLanguage } from '@/lib/language'
import { readSession } from '@/lib/session'
import { isLiveEnabled } from '@/lib/settings'
import { closeWebDetail, isWebDetailOpen, openWebDetail } from '@/browser/detailModal'
import { historyOnRoute, initWebHistory } from '@/browser/history'
import { moveFocus, type SpatialDirection } from '@/browser/spatialNav'
import { ICON_CARET, ICON_CHECK, ICON_GLOBE, iconBox } from '@/browser/icons'
import { closeWebModals, openWithAgeGate } from '@/browser/ui'
import { kindLabel } from '@/ui/card'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BRAUZER QABIĞI — üst naviqasiya · kino rejimi · klaviatura körpüsü
 * ═══════════════════════════════════════════════════════════════════════════
 * Cihaz app-ının qarşılığı sol rail-dır (`app.ts` → `buildRail`). Bölgünün
 * səbəbi `lib/platform.ts`-dədir.
 *
 * ⚠ ÜST ZOLAQ, SOL RAIL DEYİL: brauzerdə səhifə şaquli sürüşür və sol tərəfdə
 *   sabit 96px zolaq həm eni yeyir, həm də «bu sayt deyil, app-dır» hissi verir
 *   — halbuki bu variantın bütün mənası MƏHZ SAYT KİMİ davranmasıdır.
 * ⚠ BƏNDLƏRİN SIRASI: Əsas · Canlı · Axtarış · Kəşf · Tezliklə · Profil. Beşinci
 *   bənd 2026-08-22-də əlavə olundu (hero saytdakı maketə keçdi, qısayol düymələri
 *   getdi) — saytda da «Tezliklə» naviqasiya bəndidir. Qalan sıra dörd TV
 *   səthində eynidir (Android TV · Apple TV · cihaz app-ı · bu). İstifadəçi
 *   cihaz dəyişəndə bənd axtarmamalıdır. («Axtarış» 2026-08-23-dən bənd deyil —
 *   aşağıdakı qeyd; sıra adları saxlanılır ki, dörd səthin müqayisəsi itməsin.)
 * ⚠ «Canlı» BƏNDİ GÖRÜNMƏYƏ BİLƏR (2026-08-30) — `navHidden()`; bax `NAV`
 *   massivinin üstündəki qeyd.
 */

/** Yazı dayandıqdan sonra axtarış sorğusuna qədər gözləmə — saytdakı eyni dəyər. */
const SEARCH_DEBOUNCE_MS = 220

interface NavEntry {
  code: 'home' | 'live' | 'search' | 'browse' | 'collections' | 'upcoming' | 'profile'
  label: string
}

/*
  ⚠⚠ «AXTARIŞ» NAVİQASİYADAN ÇIXARILDI (sahib tələbi 2026-08-23: «veb TV kinobox-da
     axtarış menu kimi olmasın saytdakı kimi olsun, sadəcə textedit yeri olsun,
     axtarışa nəsə yazanda item kimi çıxsın»).

     Saytda da axtarış bənd deyil — üst zolağın sağındakı sahədir
     (`apps/web/src/components/HeaderSearch.tsx`) və yazdıqca nəticələr altında
     açılır. İndi burada da eynidir: `buildSearchBox` sahəni qurur.

  ⚠ MARŞRUT SİLİNMİR: `search` route-u qalır (dərin keçid və köhnə tarixçə üçün),
     sadəcə naviqasiyada görünmür.
*/
/*
  ⚠⚠ MASSİVDƏN SƏTİR SİLİNMİR / ARAYA SƏTİR SALINMIR (2026-08-30). `repaintWebNav()`
     və `applyRoute()` düyünləri `NAV[i]` ↔ `navNodes[i]` cütü ilə tapır — indeksi
     dəyişən hər dəyişiklik bəndin adını və aktiv nişanını qonşusuna keçirər.
     «Canlı» bəndi admin bayrağı ilə bağlananda ona görə SİLİNMİR, düyünü gizlədilir
     (`navHidden`) — cihaz rail-ındakı eyni qərar (`app.ts` → `railHidden`).
*/
const NAV: NavEntry[] = [
  { code: 'home', label: 'Əsas' },
  { code: 'live', label: 'Canlı' },
  { code: 'browse', label: 'Kataloq' },
  { code: 'collections', label: 'Kolleksiyalar' },
  /*
    ⚠ «TEZLİKLƏ» NAVİQASİYAYA KÖÇDÜ (2026-08-22): əvvəl yalnız hero-nun düymə
      sırasında idi, hero isə sahib tələbi ilə SAYT maketinə keçdi və orada
      qısayol düymələri yoxdur. Saytda da «Tezliklə» məhz naviqasiya bəndidir
      (`apps/web/src/components/SiteNav.tsx`), yəni bölmə əlçatmaz qalmır.
  */
  { code: 'upcoming', label: 'Tezliklə' },
  { code: 'profile', label: 'Profil' },
]

let navNodes: HTMLElement[] = []
/** Naviqasiyadan KƏNAR, amma yenə də tərcümə olunan düymələr («Daxil ol»). */
let actionNodes: Array<{ node: HTMLElement; label: string }> = []
let topbar: HTMLElement

/*
  ⚠⚠ AXTARIŞ SAHƏSİ MODUL SƏVİYYƏSİNDƏ SAXLANILIR (2026-08-30) — dil seçicisinin
     düyünləri ilə EYNİ SƏBƏBDƏN. Üst zolaq boot-da BİR DƏFƏ qurulur və sökülmür;
     `repaintWebNav()` bəndləri, aksiyaları və dil çipini yenidən yazırdı, sahənin
     `placeholder` və `aria-label` dəyərləri isə `buildSearchBox()`-da bir dəfə
     yazıldığı üçün BOOT DİLİNDƏ qalırdı. Yəni səhifə rusca, sahənin ipucu
     azərbaycanca görünürdü — yalnız tam yeniləmədən sonra düzəlirdi.
*/
let searchInput: HTMLInputElement | null = null

/** Dil seçicisinin düyünləri (üst zolaq bir dəfə qurulur, sökülmür). */
let langBtn: HTMLElement
let langCodeNode: HTMLElement
let langList: HTMLElement
let langChecks: HTMLElement[] = []
let langOpen = false

/**
 * Cari marşrut — dil dəyişəndə ekranı yenidən qurmaq üçün saxlanılır.
 * (Mətnlərin bir hissəsi API-dən `lang=` ilə gəlir, yəni yalnız zolağı yenidən
 * yazmaq bəs etmir.)
 */
let currentRoute: Route | null = null

/** Kino rejimi (player) — kətan miqyası burada saxlanılır. */
let cinema = false

/** Üst zolaqdakı «Daxil ol» — sessiya varsa gizlənir (`syncSignIn`). */
let signInNode: HTMLButtonElement | null = null

function syncSignIn(): void {
  if (signInNode) signInNode.hidden = Boolean(readSession())
}

/**
 * Naviqasiya bəndinin kliki — EYNİ səhifədirsə səhifə BAŞA qayıdır.
 *
 * ⚠ NİYƏ (sahib tələbi 2026-08-25: «sol tərəfdəki homepage klikdə əgər homepage
 *   səhifəsində scroll down edibsə yenidən home klikdə əsas ekrana başa
 *   qayıtsın»). Brauzer hədəfində `navigate` ekranı YENİDƏN QURUR, amma pəncərənin
 *   sürüşmə mövqeyi brauzerdə qalır — istifadəçi eyni yerdə görünürdü.
 * ⚠ Cihaz hədəfində eyni tələb `Screen.toTop` ilə həll olunub (`app.ts`): orada
 *   sürüşmə `transform`-ladır, pəncərə sürüşmür.
 */
function goOrTop(route: Route): void {
  if (currentRouteName() === route.name) {
    // ⚠ İki arqumentli forma (2026-09-26): `scrollTo({top, behavior})` Chrome 61+-dır, Chrome 53 TypeError atır.
    window.scrollTo(0, 0)
    return
  }
  navigate(route)
}

/** Qabığı qurur. `initShell`-dən SONRA çağırılır (üst zolaq `#stage`-dən asılı deyil). */
export function initWebShell(): void {
  topbar = byId('topbar')
  buildTopbar()
  onRouteChange(applyRoute)
  // Ünvan (#/…) və brauzerin «Geri»si — izahı `browser/history.ts`-dədir (2026-09-25).
  initWebHistory()
  bindKeyboard()
  window.addEventListener('resize', scaleCinema, false)
  topbar.hidden = false
}

function buildTopbar(): void {
  const brand = document.createElement('button')
  brand.type = 'button'
  brand.className = 'web-brand web-focusable'
  brand.setAttribute('aria-label', 'Kinobox')
  const logo = document.createElement('img')
  // ⚠ `<img>` — inline SVG DEYİL: köhnə mühərriklərdə xarici SVG dekodlanır və
  //   reflow yaratmır (cihaz player-indəki eyni qərar).
  logo.src = './logo.svg'
  logo.alt = 'Kinobox'
  brand.appendChild(logo)
  brand.onclick = () => goOrTop({ name: 'home' })
  topbar.appendChild(brand)

  const nav = el('div')
  nav.id = 'webnav'
  navNodes = []
  for (let i = 0; i < NAV.length; i++) {
    const entry = NAV[i] as NavEntry
    const node = document.createElement('button')
    node.type = 'button'
    node.className = 'web-nav__item web-focusable'
    node.appendChild(document.createTextNode(T(entry.label)))
    node.onclick = () => goOrTop({ name: entry.code } as Route)
    // ⚠ İLK KADR DA DOĞRU OLMALIDIR: zolaq boot-da bir dəfə qurulur və bu, hələ
    //   `/api/settings/public` cavabı gəlməmiş baş verir — dəyər `lib/settings.ts`
    //   keşindən gəlir (ilk açılışda «bağlı»). Cavab gələndən sonra ilk marşrut
    //   çəkilişi (`applyRoute`) vəziyyəti dəqiqləşdirir.
    node.hidden = navHidden(i)
    nav.appendChild(node)
    navNodes.push(node)
  }
  topbar.appendChild(nav)

  const actions = el('div', 'web-bar__actions')
  actionNodes = []
  actions.appendChild(buildSearchBox())
  /*
    ⚠ «DAXİL OL» HƏMİŞƏ QURULUR, GÖRÜNMƏSİ MARŞRUTLA YENİLƏNİR (2026-09-25,
      istifadəçi testi: «daxil olduqdan sonra da menyuda daxil ol düyməsi qalır,
      refreshdən sonra itir»). ƏVVƏL düymə yalnız boot-da sessiya yoxdusa yaradılırdı
      və QR girişindən sonra heç kim onu silmirdi (çıxışdan sonra da geri gəlmirdi).
      İndi `applyRoute` hər ekran dəyişimində `syncSignIn()` ilə sessiyaya baxır.
  */
  signInNode = document.createElement('button')
  signInNode.type = 'button'
  signInNode.className = 'web-nav__item is-active web-focusable'
  signInNode.appendChild(document.createTextNode(T('Daxil ol')))
  signInNode.onclick = () => navigate({ name: 'qr' })
  actions.appendChild(signInNode)
  actionNodes.push({ node: signInNode, label: 'Daxil ol' })
  syncSignIn()
  actions.appendChild(buildLangPick())
  topbar.appendChild(actions)
}

/**
 * DİL SEÇİMİ ÜST ZOLAQDA (sahib tələbi 2026-08-22: «veb TV kinobox az projedə
 * labelə dil seçimini də qoy»).
 *
 * ⚠ SAYTDAKI FORMA: qlobus + iki hərflik kod + ox, açılan siyahıda isə dilin ÖZ
 *   adı və seçilmişin yanında onay («English», «AZ» yox) — `apps/web/.../
 *   LanguageSwitcher.tsx` ilə eyni qərar, səbəbi orada yazılıb.
 * ⚠ PROFİLDƏKİ SEÇİM QALIR: eyni `lib/language.ts` dəyərini yazır, yəni iki yer
 *   bir-birini üzmür. Zolaqdakı variant sadəcə qısa yoldur — istifadəçi dil üçün
 *   Profilə girməli deyil (saytda da başlıqdadır).
 * ⚠ SİYAHI SÖKÜLMÜR: bəndlər bir dəfə qurulur, dəyişən yalnız onay işarəsidir
 *   (`langChecks`) — zolağın yenidən qurulması fokusu itirir (`repaintWebNav`
 *   şərhindəki eyni səbəb).
 */
/**
 * ÜST ZOLAQDAKI AXTARIŞ — SAYTDAKI `HeaderSearch`-in eynisi (2026-08-23).
 *
 * ⚠⚠ NİYƏ MENYU BƏNDİ DEYİL (sahib tələbi): axtarış üçün ayrıca ekrana keçmək,
 *   orada sahəni tapıb «Axtar» düyməsinə basmaq üç addımdır. Saytda bir addımdır —
 *   yazırsan, nəticə altında açılır. Sahibin sözü: «sadəcə textedit yeri olsun,
 *   axtarışa nəsə yazanda item kimi çıxsın».
 *
 * ⚠ YAZDIQCA SORĞU ATILMIR: yazı dayandıqdan 220 ms sonra gedir (saytdakı eyni
 *   dəyər). TV brauzerinin şəbəkə yığını hər hərfdə sorğu ilə ilişir.
 * ⚠ İKİ HƏRFDƏN AZ = SORĞU YOX: bir hərf bütün kataloqu qaytarır.
 * ⚠ GEC GƏLƏN CAVAB ÜSTƏLƏMİR: hər sorğunun nömrəsi var — «bat» cavabı
 *   «batman»dan sonra qayıdıb siyahını əvəz edə bilməz.
 * ⚠ NƏTİCƏ SƏTRİ FİLM SƏHİFƏSİNƏ APARIR, modal açmır — saytdakı eyni qərar
 *   (`data-content-modal="off"`): bu, kart deyil, naviqasiya siyahısıdır.
 * ⚠ AMMA YAŞ QAPISINDAN KEÇİR (2026-10-23, qırmızı xətt 3a): bax `openSuggestedContent`.
 */
function buildSearchBox(): HTMLElement {
  const box = el('div', 'web-search')

  const field = el('label', 'web-search__field')
  field.appendChild(el('span', 'web-search__icon', '⌕'))

  const input = document.createElement('input')
  input.type = 'search'
  input.className = 'web-search__input web-focusable'
  input.autocomplete = 'off'
  searchInput = input
  paintSearchBox()
  field.appendChild(input)
  box.appendChild(field)

  const list = el('div', 'web-suggest')
  box.appendChild(list)

  let timer = 0
  let ticket = 0

  const close = (): void => {
    ticket += 1
    list.className = 'web-suggest'
    clear(list)
  }

  const render = (result: SearchResult): void => {
    clear(list)
    const rows: HTMLElement[] = []

    for (let i = 0; i < result.contents.length && rows.length < 8; i++) {
      const card = result.contents[i] as ContentCard
      rows.push(suggestRow(
        card.posterUrl,
        card.title,
        // ⚠ NÖV TƏRCÜMƏ OLUNUR: server xam dəyər verir («movie») — olduğu kimi
        //   yazılsaydı siyahıda ingiliscə söz görünərdi.
        [card.year ? String(card.year) : '', kindLabel(card.kind)].filter(Boolean).join(' · '),
        false,
        () => { close(); input.value = ''; openSuggestedContent(card) },
      ))
    }

    for (let i = 0; i < result.persons.length && rows.length < 10; i++) {
      const person = result.persons[i] as SearchPerson
      rows.push(suggestRow(
        person.photoUrl,
        person.name || '—',
        person.roleName || T('Şəxs'),
        true,
        () => { close(); input.value = ''; navigate({ name: 'person', slug: person.slug }) },
      ))
    }

    if (!rows.length) {
      list.appendChild(el('div', 'web-suggest__empty', T('Nəticə tapılmadı.')))
    } else {
      for (let i = 0; i < rows.length; i++) list.appendChild(rows[i] as HTMLElement)
    }
    list.className = 'web-suggest is-open'
  }

  input.oninput = () => {
    ticket += 1
    if (timer) window.clearTimeout(timer)
    const value = input.value.trim()
    if (value.length < 2) {
      close()
      return
    }
    timer = window.setTimeout(() => {
      const mine = ++ticket
      void searchAll(value).then((result) => {
        if (mine !== ticket) return
        render(result)
      })
    }, SEARCH_DEBOUNCE_MS)
  }

  // Esc bağlayır; sahədən çıxanda da bağlanır (kliki udmamaq üçün kiçik gecikmə).
  input.onkeydown = (event) => {
    if (event.key === 'Escape' || event.keyCode === 27) close()
  }
  input.onblur = () => { window.setTimeout(close, 160) }

  return box
}

/*
  2026-10-23: AXTARIŞ NƏTİCƏSİ YAŞ QAPISINDAN VƏ «TEZLİKLƏ» YOLUNDAN KEÇİR.
    NƏ TAPILDI: sətir birbaşa `navigate({ name: 'detail' })` edirdi — kart, hero və siyahılar
    `openWithAgeGate`-dən keçir, üst zolağın axtarışı isə keçmirdi.
    ƏVVƏL: yaşı çatmayan hesab 18+ filmin səhifəsini axtarışdan açırdı (qırmızı xətt 3a:
    «bağlanan yalnız AÇILIŞDIR»); plana salınmış film səhifəsi isə 404 sorğusundan sonra gəlirdi.
    NİYƏ BELƏ: səhifəyə keçid qərarı (saytdakı kimi) qalır, yalnız ondan ƏVVƏL qapı işləyir.
    «Tezliklə» kartı isə film modalının yüngül formasını açır (detal ucu onu vermir).
*/
function openSuggestedContent(card: ContentCard): void {
  if (card.isComingSoon) {
    openWebDetail(card)
    return
  }
  openWithAgeGate(card, () => navigate({ name: 'detail', slug: card.slug }))
}

/** Açılan siyahının bir sətri — şəkil + ad + alt sətir. */
function suggestRow(
  image: string | null,
  name: string,
  meta: string,
  round: boolean,
  onOpen: () => void,
): HTMLElement {
  const row = document.createElement('button')
  row.type = 'button'
  row.className = 'web-suggest__item web-focusable'

  const thumb = document.createElement('img')
  thumb.className = round ? 'web-suggest__thumb web-suggest__thumb--round' : 'web-suggest__thumb'
  thumb.alt = ''
  const url = mediaUrl(image, IMAGE_WIDTH.avatar)
  if (url) thumb.src = url
  row.appendChild(thumb)

  const body = el('span', 'web-suggest__body')
  body.appendChild(el('span', 'web-suggest__name', name))
  if (meta) body.appendChild(el('span', 'web-suggest__meta', meta))
  row.appendChild(body)

  // ⚠ `onmousedown`, `onclick` DEYİL: `onblur` siyahını bağlayır və klik
  //   hadisəsi ondan SONRA gəlir — sətir seçilməzdi.
  row.onmousedown = (event) => { event.preventDefault(); onOpen() }
  return row
}

function buildLangPick(): HTMLElement {
  const root = el('div', 'web-langpick')

  langBtn = el('button', 'web-langpick__btn web-focusable')
  ;(langBtn as HTMLButtonElement).type = 'button'
  langBtn.setAttribute('aria-haspopup', 'listbox')
  langBtn.setAttribute('aria-expanded', 'false')
  langBtn.setAttribute('aria-label', T('Dil'))
  langBtn.appendChild(iconBox(ICON_GLOBE))
  langCodeNode = el('span', 'web-langpick__code', readLanguage().toUpperCase())
  langBtn.appendChild(langCodeNode)
  const caret = iconBox(ICON_CARET)
  caret.className += ' web-langpick__caret'
  langBtn.appendChild(caret)
  langBtn.onclick = () => openLang(!langOpen)
  root.appendChild(langBtn)

  langList = el('div', 'web-langpick__list')
  langList.setAttribute('role', 'listbox')
  langList.hidden = true
  langChecks = []
  for (let i = 0; i < LANGUAGES.length; i++) {
    const language = LANGUAGES[i]
    if (!language) continue
    const option = el('button', 'web-langpick__option web-focusable')
    option.type = 'button'
    option.setAttribute('role', 'option')
    option.appendChild(el('span', 'web-langpick__code', language.code.toUpperCase()))
    option.appendChild(el('span', 'web-langpick__native', language.native))
    const check = iconBox(ICON_CHECK)
    check.className += ' web-langpick__check'
    check.hidden = language.code !== readLanguage()
    option.appendChild(check)
    option.onclick = () => pickLanguage(language.code)
    langChecks.push(check)
    langList.appendChild(option)
  }
  root.appendChild(langList)

  /*
    ⚠ KƏNARA KLİK + ESC BAĞLAYIR — açılan siyahının standart davranışı. Dinləyici
      `document`-dədir və BİR DƏFƏ qurulur: siyahı bağlıdırsa dərhal çıxır, yəni
      hər klikdə iş görmür.
    ⚠ `contains` YOXLAMASI: düymənin öz kliki bura da düşür, yoxsa siyahı açılan
      anda bağlanardı.
  */
  document.addEventListener(
    'mousedown',
    (event) => {
      if (!langOpen) return
      const target = event.target as Node | null
      if (target && root.contains(target)) return
      openLang(false)
    },
    false,
  )

  return root
}

function openLang(on: boolean): void {
  if (langOpen === on) return
  langOpen = on
  langList.hidden = !on
  setClass(langBtn, 'is-open', on)
  langBtn.setAttribute('aria-expanded', on ? 'true' : 'false')
}

function pickLanguage(code: string): void {
  openLang(false)
  if (code === readLanguage()) return
  writeLanguage(code)
  repaintWebNav()
  /*
    ⚠⚠ ANA EKRANIN DATASI DA YENİDƏN ÇƏKİLİR (2026-08-30) — `screens/profile.ts`-dəki
       eyni çağırış, səbəbi orada və `browser/main.ts` → `reloadHomeData`-dadır:
       kataloq boot-da BİR DƏFƏ alınır, yəni yalnız yenidən naviqasiya etsək film
       adları və blok başlıqları köhnə dildə qalır.
    ⚠ AŞAĞIDAKI `navigate`-DƏN ƏVVƏLDİR və GÖZLƏNMİR: sorğu ekranın qurulması ilə
      paralel gedir; cavab gələndə ana ekran ekrandadırsa `reloadHomeData` onu
      ikinci dəfə qurur.
  */
  void reloadForLanguage()

  /*
    ⚠ EKRAN YENİDƏN QURULUR: film adı/təsviri API-dən `lang=` ilə gəlir, yəni
      yalnız zolağı tərcümə etmək yarımçıq nəticə verir (`account.ts`-dəki eyni
      qərar). `replace` ilə — 20 dil dəyişikliyindən sonra yığın şişməsin.
    ⚠ OYNADICI İSTİSNADIR: kino rejimində yenidən qurmaq oynayan filmi başdan
      başladardı. Zolaq onsuz da tərcümə olundu; qalan mətnlər növbəti ekranda
      düzgün gələcək.
  */
  const route = currentRoute
  if (route && route.name !== 'player') navigate(route, true)
}

/**
 * ÜST ZOLAĞIN MƏTNLƏRİNİ CARİ DİLDƏ YENİDƏN YAZIR.
 *
 * ⚠ NİYƏ LAZIMDIR (2026-08-16 defekti — TestingClaude veb TV 42). ÖLÇÜLDÜ:
 *   Profildə «English» seçiləndə səhifə tərcümə olunurdu («Profile · Account …»),
 *   ÜST ZOLAQ isə azərbaycanca qalırdı («Əsas · Canlı · Axtarış · Kəşf · Profil»);
 *   yalnız TAM YENİLƏMƏDƏN sonra düzəlirdi. Səbəb: zolaq boot-da BİR DƏFƏ qurulur
 *   (`buildTopbar` → `T(entry.label)`), dil dəyişəndə isə yalnız EKRAN yenidən
 *   çəkilirdi (`navigate({name:'profile'}, true)`).
 * ⚠ ZOLAQ SÖKÜLMÜR, YALNIZ MƏTN DƏYİŞİR: yenidən qurmaq fokusu itirər (pultla
 *   dil seçən istifadəçi fokusu sənədin başında tapardı) və loqonun şəklini
 *   təzədən yüklədərdi. `setText` mətni yalnız fərqlidirsə yazır.
 * ⚠ AKTİV BƏND SİNFİNƏ TOXUNULMUR — o, marşrutdan asılıdır (`applyRoute`).
 */
/**
 * Axtarış sahəsinin İKİ tərcümə olunan sahəsi — ipucu və ekran-oxuyucu adı.
 * ⚠ TƏK YERDƏ: qurulma (`buildSearchBox`) və yenidən boyanma (`repaintWebNav`)
 *   eyni funksiyanı çağırır ki, biri dəyişəndə o biri arxada qalmasın.
 */
function paintSearchBox(): void {
  if (!searchInput) return
  const hint = T('Film, serial, aktyor…')
  if (searchInput.placeholder !== hint) searchInput.placeholder = hint
  searchInput.setAttribute('aria-label', T('Axtarış'))
}

export function repaintWebNav(): void {
  for (let i = 0; i < navNodes.length; i++) {
    const entry = NAV[i]
    if (entry) setText(navNodes[i] as HTMLElement, T(entry.label))
  }
  for (let i = 0; i < actionNodes.length; i++) {
    const action = actionNodes[i]
    if (action) setText(action.node, T(action.label))
  }

  // Üst zolaqdakı axtarış sahəsi (2026-08-30) — izahı `searchInput`-un üstündədir.
  paintSearchBox()

  // Dil seçicisi: kod + onay işarəsi (seçim Profildən də dəyişə bilər).
  if (langCodeNode) setText(langCodeNode, readLanguage().toUpperCase())
  if (langBtn) langBtn.setAttribute('aria-label', T('Dil'))
  for (let i = 0; i < langChecks.length; i++) {
    const check = langChecks[i]
    const language = LANGUAGES[i]
    if (check && language) check.hidden = language.code !== readLanguage()
  }
}

/**
 * Naviqasiya bəndi gizlidirmi — cihaz rail-ındakı `railHidden()`-in əkizi.
 *
 * ⚠ HAZIRDA YALNIZ «Canlı»: admin `features.live_enabled` açarını «true» etməyibsə
 *   bənd görünmür (sahib tələbi 2026-08-30, defolt BAĞLI — `lib/settings.ts`).
 * ⚠ UŞAQ ŞƏRTİ (2026-09-25, BusinessRequirement 9.3): uşaq profilində «Canlı»
 *   HEÇ BİR səthdə göstərilmir — xətti yayımdır, nə yaş süzgəci, nə cədvəl var.
 *   ƏVVƏL brauzer qabığı yalnız admin açarına baxırdı; cihaz qabığı (`app.ts`
 *   `railHidden`) uşaq şərtini onsuz da tətbiq edirdi — iki qabıq fərqli idi.
 *   Açar `<html data-kids>` atributudur (profil seçimində `lib/profiles.ts` qurur).
 */
function navHidden(index: number): boolean {
  const entry = NAV[index]
  if (!entry) return false
  // «Tezliklə» — bölmədə film yoxdursa bənd gizlənir (sahib tələbi 2026-10-02).
  if (entry.code === 'upcoming') return !isUpcomingAvailable()
  if (entry.code !== 'live') return false
  return document.documentElement.hasAttribute('data-kids') || !isLiveEnabled()
}

/**
 * Marşrut dəyişəndə: aktiv bənd · kino rejimi · səhifənin başına qayıtmaq.
 *
 * ⚠ SƏHİFƏ BAŞINA QAYTARMAQ MƏCBURİDİR: doğma sürüşmə `document`-dədir və ekran
 *   dəyişəndə ÖZÜ sıfırlanmır — kataloqun ortasından filmə keçəndə yeni səhifə
 *   ortadan açılırdı (istifadəçiyə «boş səhifə» kimi görünür).
 */
// 2026-10-11: yeni ekrana gecikmiş premyera sorğusu modal aça bilməsin.
function applyRoute(route: Route): void {
  closeWebDetail()
  // 2026-10-23: yaş pəncərələri yeni ekranın üstündə qalıb fokusu tutmasın (izah `ui.ts`).
  closeWebModals()
  currentRoute = route
  openLang(false)
  syncSignIn()
  historyOnRoute(route)
  for (let i = 0; i < navNodes.length; i++) {
    const node = navNodes[i] as HTMLElement
    setClass(node, 'is-active', (NAV[i] as NavEntry).code === route.name)
    // ⚠ GİZLƏTMƏ BURADA DA TƏTBİQ OLUNUR: parametr sorğusu boot `Promise.all`-undadır
    //   (`browser/main.ts`), yəni ilk `navigate()` ondan SONRA gəlir — bu, bayrağın
    //   ilk dəqiq cavabının zolağa çatdığı yeganə nöqtədir.
    node.hidden = navHidden(i)
  }

  // ⚠ GİRİŞ QAPISI (MYTasks 214): QR ekranında naviqasiya ÜMUMİYYƏTLƏ görünmür —
  //   əks halda girişsiz istifadəçi menyudan kataloqa keçə bilərdi.
  topbar.hidden = route.name === 'qr'

  setCinema(route.name === 'player')
  if (!cinema) window.scrollTo(0, 0)
}

/**
 * Kino rejimi — ortaq oynadıcının qabı.
 *
 * ⚠ NİYƏ SİNİF + MİQYAS (izah `styles/web.css`-dəki `.is-cinema` blokunda):
 *   `screens/player.ts` sabit 1920×1080 ölçülərlə çəkilir. Kətanı miqyaslamaq
 *   həmin 2300 sətri olduğu kimi yenidən işlətməyə imkan verir — brauzer üçün
 *   ayrıca player yazmaq keyfiyyət/altyazı/səs menyusunu, DRM-i və canlı pin-i
 *   itirmək demək olardı.
 */
function setCinema(on: boolean): void {
  if (cinema === on) return
  cinema = on
  setClass(document.body, 'is-cinema', on)
  if (on) {
    scaleCinema()
    return
  }
  const stage = byId('stage')
  stage.style.transform = ''
}

function scaleCinema(): void {
  if (!cinema) return
  const stage = byId('stage')
  const scale = Math.min(window.innerWidth / 1920, window.innerHeight / 1080)
  const offsetX = Math.round((window.innerWidth - 1920 * scale) / 2)
  const offsetY = Math.round((window.innerHeight - 1080 * scale) / 2)
  // ⚠ SIRA VACİBDİR: `transform-origin: 0 0` olduğu üçün əvvəl sürüşdürür, sonra
  //   miqyaslayır — tərsi olsa boşluq da miqyaslanar və kətan mərkəzdən çıxar.
  const value = 'translate(' + offsetX + 'px,' + offsetY + 'px) scale(' + scale + ')'
  stage.style.transform = value
  stage.style.setProperty('-webkit-transform', value)
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * KLAVİATURA KÖRPÜSÜ
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠⚠ `lib/input.ts`-in KADR NASOSU BURADA İŞLƏDİLMİR — və bu, bölgünün ƏSAS
 *    texniki səbəbidir. Həmin nasos ox düymələrini udur (`preventDefault`) və
 *    fokusu öz şəbəkəsi ilə hesablayır. Brauzerdə isə ox/Tab BRAUZERİN öz
 *    naviqasiyasıdır: TV brauzeri onları onsuz da özü işlədir, geri almaq
 *    mümkün deyil (2026-08-10 real cihaz testi). Onları udmaq = idarəetməni
 *    TAMAMİLƏ öldürmək.
 *
 * ⚠ KİNO REJİMİ İSTİSNADIR: player ekranında doğma fokus gəzəcək element yoxdur
 *   (o, öz nəzarət panelini pult məntiqi ilə çəkir), ona görə orada düymələr
 *   ötürülür. Yəni körpü YALNIZ player açıq olanda işləyir.
 * ⚠ `Escape`/`Backspace` hər yerdə «geri»dir — brauzer geri jesti ilə eyni
 *   gözlənti (dead-end yoxdur qaydası).
 * ⚠ OX DÜYMƏLƏRİ (2026-10-01, TestingAI2 veb TV 29): masaüstü brauzerdə oxlar
 *   yalnız səhifəni sürüşdürürdü, fokus heç yerə keçmirdi — ölçmə və həll
 *   `browser/spatialNav.ts`-dədir. TV brauzerində hadisə sənədə çatmır, yəni
 *   orada davranış dəyişmir; Tab doğma qalır.
 */
function bindKeyboard(): void {
  // Tizen «Back»i qeydiyyatdan keçirir; brauzerdə funksiya səssizcə heç nə etmir.
  registerPlatformKeys()

  document.addEventListener(
    'keydown',
    (event: KeyboardEvent) => {
      // Yazı sahəsində düymələr mətnindir — naviqasiyaya toxunulmur.
      const target = event.target as HTMLElement | null
      const tag = target && target.tagName ? target.tagName.toUpperCase() : ''
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

      const key = toRemoteKey(event)
      if (key === 'unknown') return

      /*
        ⚠ AÇIQ DİL SİYAHISI «GERİ»NİN QARŞISINI KƏSİR: Esc/Back əvvəlcə siyahını
          bağlamalıdır, ekranı DEYİL — əks halda dil siyahısını bağlamaq istəyən
          istifadəçi bir ekran geri atılırdı (açılan siyahının standart davranışı).
      */
      if (key === 'back' && langOpen) {
        event.preventDefault()
        openLang(false)
        return
      }

      if (cinema) {
        // ⚠ Tab-ı ötürmürük: `toRemoteKey` onu sol/sağ kimi oxuyur, halbuki kino
        //   rejimində fokus gəzişməsi lazım deyil və Tab-ı udmaq brauzerin
        //   çıxış yolunu bağlayardı.
        if (event.keyCode === 9) return
        if (isNavigationKey(key) || key === 'enter' || key === 'back') event.preventDefault()
        dispatchKey(key)
        return
      }

      if (key === 'back') {
        event.preventDefault()
        // Ekran «geri»ni özü tuta bilər (məsələn açıq PIN pad-ı) — izahı `Screen.onBack`.
        if (backHandledByScreen()) return
        back()
        return
      }

      // Yalnız əsl ox düymələri (37–40): Tab/PageUp kimi düymələr də `toRemoteKey`-də
      // istiqamətə çevrilir, onlar isə doğma qalmalıdır. Modifikatorlu ox — brauzerin.
      const code = event.keyCode
      if (code < 37 || code > 40 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
      if (event.defaultPrevented || !isNavigationKey(key)) return
      if (moveFocus(key as SpatialDirection, focusScope())) event.preventDefault()
    },
    false,
  )
}

/**
 * Ox naviqasiyasının əhatəsi — açıq qat varsa fokus onun içindən çıxmır
 * (dil siyahısı → yaş qapısı pəncərəsi → film modalı → bütün səhifə).
 */
function focusScope(): ParentNode {
  // 2026-10-06: PIN qatında oxlar arxadakı profillərə keçirdi. Doğma
  // düymələrin naviqasiyası yalnız açıq pad daxilində qalır.
  const pinPad = document.querySelector('.pinpad')
  if (pinPad) return pinPad
  if (langOpen) return langList
  const modals = document.querySelectorAll('.web-modal')
  const topModal = modals.length > 0 ? modals[modals.length - 1] : null
  if (topModal) return topModal
  if (isWebDetailOpen()) {
    const dm = document.querySelector('.web-dm')
    if (dm) return dm
  }
  return document.body
}
